import { Injectable } from '@angular/core';
import { Subscription, Subject } from 'rxjs';

import { LeaderboardHttpService } from '../http/http.service';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Pagination } from '@app/core/model/pagination.model';
import { UserSimpleGamification, UserSimpleGoal } from '../../model/user.model';
import { UserService } from '@app/user';

import { BroadcastService, BroadcastMessage } from '@app/core/service';

@Injectable()
export class LeaderboardService {
    /**
     * Loading Flag
     */
    loading = false;

    /**
     * Leaderboard
     */
    _leaderboard: {
        gamification: {
            [key: string]: {
                [key: number]: Array<UserSimpleGamification>;
            };
        };
        goal: {
            [key: string]: {
                [key: number]: Array<UserSimpleGoal>;
            };
        };
    } = {
        gamification: {},
        goal: {},
    };

    /**
     * Total Items
     */
    _total: {
        gamification: {
            [key: string]: number;
        };
        goal: {
            [key: string]: number;
        };
    } = {
        gamification: {},
        goal: {},
    };

    /**
     * Ranges
     */
    ranges: Array<string> = ['week', 'month', 'yeartodate', 'alltime'];

    /**
     * Types
     */
    types: Array<string> = ['goal'];

    /**
     * Range FormControl
     */
    form: UntypedFormGroup;

    /**
     * Pagination
     */
    _pagination: Pagination = {
        offset: 0,
        limit: 20,
    };

    /**
     * viewed Leaderboard
     */
    viewedPart: Array<UserSimpleGamification | UserSimpleGoal> = null;

    /**
     * Subject
     */
    private updatesEvent = new Subject();

    /**
     * Subscriptions
     */
    private subscriptions = new Subscription();

    constructor(
        private leaderboardHttpService: LeaderboardHttpService,
        private userService: UserService,
        private broadcastService: BroadcastService,
    ) {}

    init(): void {
        // gamification
        if (this.userService.access.includes('gamification')) {
            this.types.unshift('gamification');
        }
        this.initForm();
        this.subscriptions.add(this.broadcastService.broadcasts.subscribe((broadcast) => this.broadcasts(broadcast)));
    }

    destroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Subject
     */
    get updates() {
        return this.updatesEvent.asObservable();
    }

    /**
     * Get Leaderboard
     */
    get leaderboard() {
        // create empty tree
        this.createEmptyTree();
        // check if loaded
        if (this._leaderboard[this.form.value.type][this.form.value.range][this.pagination.offset]) {
            this.viewedPart = this._leaderboard[this.form.value.type][this.form.value.range][this.pagination.offset];
        } else {
            this.loadList();
        }
        // return viewed part
        return this.viewedPart;
    }

    /**
     * Get Total
     */
    get total() {
        return this._total[this.form.value.type][this.form.value.range] ? this._total[this.form.value.type][this.form.value.range] : 0;
    }

    /**
     * Set Pagination
     */
    set pagination(pagination: Pagination) {
        this._pagination = pagination;
    }

    /**
     * Get Pagination
     */
    get pagination() {
        return this._pagination;
    }

    /**
     * Load Leaderboard
     */
    loadList(): void {
        if (this.loading) {
            return;
        }

        const options = this.form.value;
        const offset = this.pagination.offset;

        this.loading = true;
        this.updatesEvent.next(true);

        switch (options.type) {
            case 'gamification':
                this.subscriptions.add(
                    this.leaderboardHttpService
                        .getLeaderboardGamification(options.range, this.pagination.limit, this.pagination.offset)
                        .subscribe((data) => this.handleResponse(data.users, data.total, options.type, options.range, offset)),
                );
                break;
            case 'goal':
                this.subscriptions.add(
                    this.leaderboardHttpService
                        .getLeaderboardGoal(options.range, this.pagination.limit, this.pagination.offset)
                        .subscribe((data) => this.handleResponse(data.users, data.total, options.type, options.range, offset)),
                );
                break;
        }
    }

    /**
     * Reset Pagination and Form
     */
    reset(): void {
        this.form.patchValue(
            {
                type: this.types[0],
                range: this.ranges[0],
            },
            { emitEvent: false },
        );
        this.pagination = {
            offset: 0,
            limit: 10,
        };
        this.viewedPart = null;
    }

    /**
     * Response Handler
     * @param users List of Users
     * @param total Total number of Users
     * @param type gamification or goal
     * @param range week, month or all
     * @param offset offset of the query
     */
    private handleResponse(users: Array<UserSimpleGamification | UserSimpleGoal>, total: number, type: string, range: string, offset: number): void {
        this._leaderboard[type][range][offset] = users;
        this._total[type][range] = total;
        this.loading = false;
        this.updatesEvent.next(true);
    }

    /**
     * Init Form
     */
    private initForm(): void {
        // form
        this.form = new UntypedFormGroup({
            type: new UntypedFormControl(this.types[0]),
            range: new UntypedFormControl(this.ranges[0]),
        });
        // subscription
        this.subscriptions.add(
            this.form.valueChanges.subscribe(() => {
                this.pagination = {
                    offset: 0,
                    limit: 10,
                };
                this.viewedPart = null;
                this.updatesEvent.next(true);
            }),
        );
    }

    /**
     * Create Empty tree
     */
    private createEmptyTree(): void {
        // type
        if (!this._leaderboard[this.form.value.type]) {
            this._leaderboard[this.form.value.type] = {};
            this._total[this.form.value.type] = {};
        }
        // range
        if (!this._leaderboard[this.form.value.type][this.form.value.range]) {
            this._leaderboard[this.form.value.type][this.form.value.range] = {};
            this._total[this.form.value.type][this.form.value.range] = null;
        }
        // pagination
        if (!this._leaderboard[this.form.value.type][this.form.value.range][this.pagination.offset]) {
            this._leaderboard[this.form.value.type][this.form.value.range][this.pagination.offset] = null;
        }
    }

    /**
     * Broadcast
     */
    private broadcasts(broadcast: BroadcastMessage): void {
        switch (broadcast.message) {
            case 'episode-completed':
            case 'discussion-topic-created':
            case 'discussion-topic-reply-created':
            case 'discussion-reply-reply-created':
            case 'user-self-edited':
                this.reset();
                break;
        }
    }
}
